import React from 'react';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './CommunityRulesPage.module.css';

const RuleCard = ({ number, heading, children }) => (
  <section className={css.ruleCard}>
    <span className={css.ruleBadge} aria-hidden="true">
      {number}
    </span>
    <div className={css.ruleContent}>
      <h2>{heading}</h2>
      {children}
    </div>
  </section>
);

/**
 * CommunityRulesPage renders PageHop's Community Rules using the current PageHop
 * interior-page visual system (hero + stacked rule cards + closing panel).
 */
const CommunityRulesPage = () => (
  <Page
    title="Community Rules | PageHop Books"
    description="PageHop's Community Rules for buyers, sellers, and everyone using the marketplace."
  >
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <div className={css.content}>
          <header className={css.hero}>
            <h1>Community Rules</h1>
            <p className={css.lede}>
              PageHop is a marketplace built for readers, collectors, sellers and bookish people
              across Canada.
            </p>
            <p className={css.lede}>
              We want it to feel friendly, trustworthy and easy to use — which means everyone has a
              part to play.
            </p>
            <p className={css.lede}>
              These rules apply anywhere you interact on PageHop, including listings, shops,
              messages, reviews and community features.
            </p>
            <span className={css.accent} aria-hidden="true" />
          </header>

          <div className={css.rules}>
            <RuleCard number="01" heading="Be a good shelf neighbour">
              <p>Treat other members with respect.</p>
              <p>That means no:</p>
              <ul>
                <li>harassment, bullying or threats</li>
                <li>hate speech or discriminatory behaviour</li>
                <li>abusive, sexually harassing or deliberately intimidating messages</li>
                <li>repeated unwanted contact</li>
                <li>sharing someone else’s private information</li>
                <li>impersonating another person or business</li>
              </ul>
              <p>Disagreements happen. Keep them civil.</p>
            </RuleCard>

            <RuleCard number="02" heading="List honestly">
              <p>Buyers should be able to trust what they see.</p>
              <p>Listings must accurately describe:</p>
              <ul>
                <li>the item being sold</li>
                <li>its condition</li>
                <li>any damage, markings or missing pieces</li>
                <li>whether it is signed, numbered, limited, exclusive or otherwise collectible</li>
                <li>what is actually included in the purchase</li>
              </ul>
              <p>Use photos that accurately represent the item whenever possible.</p>
              <p>
                Don’t hide flaws or use misleading descriptions to make something appear rarer,
                newer or more valuable than it is.
              </p>
            </RuleCard>

            <RuleCard number="03" heading="Authenticity matters">
              <p>
                Do not knowingly list counterfeit, pirated, stolen or fraudulently represented
                items.
              </p>
              <p>This includes things like:</p>
              <ul>
                <li>fake signatures</li>
                <li>counterfeit special editions</li>
                <li>unauthorized reproductions</li>
                <li>pirated books or digital files</li>
                <li>merchandise that infringes someone else’s intellectual property rights</li>
                <li>items you do not own or have the right to sell</li>
              </ul>
              <p>
                If you’re unsure about an item’s authenticity, say so clearly rather than presenting
                it as verified.
              </p>
            </RuleCard>

            <RuleCard number="04" heading="Keep transactions on PageHop">
              <p>
                If you found the buyer or seller through PageHop, complete the transaction through
                PageHop.
              </p>
              <p>
                Don’t use messages, listings or profile information to move a sale off-platform in
                order to avoid PageHop fees or marketplace protections.
              </p>
              <p>
                PageHop relies on marketplace fees to keep the platform running, support buyers and
                sellers, maintain the site and continue improving the service. Taking transactions
                off-platform may seem harmless, but it directly undermines the marketplace everyone
                is using.
              </p>
              <p>
                Keeping transactions on PageHop also helps protect both buyers and sellers if
                something goes wrong.
              </p>
            </RuleCard>

            <RuleCard number="05" heading="Sellers: follow through">
              <p>When someone buys from you:</p>
              <ul>
                <li>package the item with reasonable care</li>
                <li>ship within the timeframe you’ve committed to</li>
                <li>provide accurate shipping information where required</li>
                <li>communicate if there is an unexpected delay</li>
                <li>don’t mark something as shipped when it hasn’t been</li>
              </ul>
              <p>
                If you can no longer complete an order, deal with it promptly rather than leaving
                the buyer wondering what happened.
              </p>
            </RuleCard>

            <RuleCard number="06" heading="Buyers: buy in good faith">
              <p>Don’t:</p>
              <ul>
                <li>place orders you have no intention of completing</li>
                <li>abuse refund, dispute or chargeback systems</li>
                <li>falsely claim an item wasn’t received or was misrepresented</li>
                <li>pressure sellers into off-platform transactions</li>
                <li>harass sellers over pricing</li>
              </ul>
              <p>It’s okay to ask questions. It’s not okay to badger someone into a sale.</p>
            </RuleCard>

            <RuleCard number="07" heading="Reviews should be real">
              <p>Reviews should reflect a genuine transaction or experience on PageHop.</p>
              <p>Don’t:</p>
              <ul>
                <li>leave fake reviews</li>
                <li>exchange positive reviews</li>
                <li>use reviews to threaten or retaliate against another member</li>
                <li>manipulate ratings through multiple accounts</li>
              </ul>
              <p>Stick to what actually happened.</p>
            </RuleCard>

            <RuleCard number="08" heading="No spam or manipulation">
              <p>Don’t use PageHop to:</p>
              <ul>
                <li>send unsolicited promotional messages</li>
                <li>post the same listing repeatedly to game search results</li>
                <li>manipulate favourites, ratings, reviews or marketplace activity</li>
                <li>create deceptive accounts</li>
                <li>scrape or collect member information without permission</li>
              </ul>
            </RuleCard>

            <RuleCard number="09" heading="Respect privacy">
              <p>
                Don’t publish or share another member’s personal information without their
                permission.
              </p>
              <p>Keep sensitive information out of listings, reviews and public profile areas.</p>
              <p>
                If someone sends you personal information as part of a transaction, use it only for
                the purpose it was provided.
              </p>
            </RuleCard>

            <RuleCard number="10" heading="If something doesn’t feel right, report it">
              <p>
                If you see a listing, message, review or account that appears to break these rules,
                report it rather than trying to police the situation yourself.
              </p>
              <p>We may review reported activity and take action where appropriate.</p>
              <p>Depending on the situation, that may include:</p>
              <ul>
                <li>removing content or listings</li>
                <li>issuing a warning</li>
                <li>restricting marketplace features</li>
                <li>cancelling transactions</li>
                <li>suspending or closing an account</li>
              </ul>
              <p>Serious or repeated violations may result in immediate account removal.</p>
            </RuleCard>

            <RuleCard number="11" heading="A little common sense goes a long way">
              <p>No set of rules can cover every situation.</p>
              <p>
                We may take action where behaviour clearly undermines the safety, integrity or trust
                of the PageHop marketplace, even if that exact behaviour isn’t specifically listed
                above.
              </p>
              <p>
                These Community Rules work alongside PageHop’s{' '}
                <NamedLink name="TermsOfServicePage">Terms of Service</NamedLink>,{' '}
                <NamedLink name="PrivacyPolicyPage">Privacy Policy</NamedLink>, listing requirements
                and other marketplace policies.
              </p>
            </RuleCard>
          </div>

          <section className={css.closing}>
            <h2>Good shelves make good neighbours. 💜</h2>
            <p>Thanks for helping us build a marketplace Canadian readers can trust.</p>
          </section>
        </div>
      </main>
    </LayoutSingleColumn>
  </Page>
);

export default CommunityRulesPage;
